import { useEffect, useState, useRef } from "react"

//styles
import filterStyles from "../../styles/CustomFilter.module.css"

//primeReact
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { MultiSelect } from "primereact/multiselect"
import { Calendar } from "primereact/calendar"
import { Checkbox } from "primereact/checkbox"
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

// Custom components
import CustomSort from "../CustomSort/CustomSort"
import { FilterLocation, FilterTypes } from "./FilterConstants"
import { classNames } from "primereact/utils"

const _ = require('lodash');

const FilterItemContainer = ({ filter, children }) => {
  return (
    <div className={classNames(filterStyles.filterItemContainer, filter.location === FilterLocation.bar && filterStyles.barFilterItem)}>
      {((filter.location === FilterLocation.panel) || (filter.location === FilterLocation.bar && filter.showBarLabel)) && (
        <div className={filterStyles.headerLabel} >{filter.label}</div>
      )}
      {children}
    </div>
  )
}

/*
Example filter item definition
[
  {
      key: "unique_key",
      label: "Filter label",
      defaultValue: [], // [] - multiselect, null - dropdown
      currentValue: [],
      type: "multiselect",
      location: FilterLocation.bar,
      options: organizationOptions, // defined in a custom function
      placeholder: "Select organization",
      showClear: false,
      showFilter: false,
  },
  {
      key: "dropdown_filter",
      label: "Dropdown Test",
      defaultValue: null,
      currentValue: null,
      type: "dropdown",
      location: FilterLocation.panel,
      options: [...],
      placeholder: "Dropdown Test",
      showClear: true,
      showFilter: true
  },
  {
      key: "text_filter",
      label: "Text Test",
      defaultValue: "",
      currentValue: "",
      type: "text",
      location: FilterLocation.panel,
      placeholder: "Text Test"
  },
  {
      key: "checkbox_filter",
      label: "Check Test 2",
      defaultValue: false,
      currentValue: false,
      type: "checkbox",
      location: FilterLocation.panel,
      placeholder: "Check Test"
  },
  {
      key: "calendar_filter",
      label: "Calendar Test",
      defaultValue: null,
      currentValue: null,
      type: "calendar",
      location: FilterLocation.panel,
      placeholder: "Calendar Test",
      showTime: true,
      hourFormat: "12",
      showIcon: true,
      timeOnly: false
  }
]

*/

const CustomFilter = ({
  filters, // A JSON object defining filter options, and values
  setFilters, // Event handler to update filters
  sorting, // A JSON object defining sorting options, direction and state - if null, no sort component will be rendered
  setSorting, // Event handler to update sorting
  singleSortOnly,
  loading, // A boolean indicating if the parent form is loading,
  applyFilters, // A function (defined on the parent) to refresh rendered data
  children
}) => {

  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const skipEvents = useRef({})
  const [pendingApply, setPendingApply] = useState(false)

  useEffect(() => {
    if (pendingApply) {
      setPendingApply(false)
      applyFilters()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sorting])


  const isDefined = (value) => {
    return value !== undefined
  }

  function pushSkipEvent(component_key, event_name) {
    skipEvents.current[`${component_key}_${event_name}`] = true
  }

  function popSkipEvent(component_key, event_name) {
    if (skipEvents.current[`${component_key}_${event_name}`]) {
      delete skipEvents.current[`${component_key}_${event_name}`]
      return true
    }
    return false
  }

  // Filter Types
  function TextFilter(filter) {
    return (
      <FilterItemContainer key={filter.key} filter={filter}>
        <InputText
          className={classNames(filterStyles.textFilter, filterStyles.filterItemBorder)}
          value={isDefined(filter.tempValue) ? filter.tempValue : filter.currentValue}
          placeholder={filter.placeholder}
          onChange={e => updateFilters(filter, e.target.value)}
          onBlur={() => {
            applyBarFilters(filters)
          }}
          onKeyDown={e => {
            if (e.key === "Enter") {
              applyBarFilters(filters)
            }
          }}
        />
      </FilterItemContainer>
    )
  }

  function MultiselectFilter(filter) {
    return (
      <FilterItemContainer key={filter.key} filter={filter}>
        <MultiSelect
          className={classNames(filterStyles.multiselectFilter, filterStyles.filterItemBorder)}
          display="chip"
          panelHeaderTemplate={<></>}
          value={isDefined(filter.tempValue) ? filter.tempValue : filter.currentValue}
          options={filter.options}
          showClear={filter.showClear}
          placeholder={filter.placeholder}
          optionLabel={"name"}
          optionValue={"code"}
          maxSelectedLabels={3}
          filter={filter.showFilter}
          disabled={loading}
          onChange={e => {
            if (!popSkipEvent(filter.key, "onChange")) {
              updateFilters(filter, e.value)
            }
          }}
          onHide={() => {
            applyBarFilters(filters)
          }}
          onRemove={e => {
            pushSkipEvent(filter.key, "onChange") // Skip the next onChange event (onRemove 'e' does not contain stopPropagation())
            const updatedFilters = updateFilters(filter, e.value, true) // Update filters, but don't set "applied" flags
            filter.location === FilterLocation.bar && applyBarFilters(updatedFilters, true)
          }}
        />
      </FilterItemContainer>
    )
  }

  function DropDownFilter(filter) {
    return (
      <FilterItemContainer key={filter.key} filter={filter}>
        <Dropdown
          className={classNames(filterStyles.dropdownFilter, filterStyles.filterItemBorder)}
          value={isDefined(filter.tempValue) ? filter.tempValue : filter.currentValue}
          options={filter.options}
          optionLabel={"name"}
          optionValue={"code"}
          placeholder={filter.placeholder}
          filter={filter.showFilter}
          showClear={filter.showClear}
          disabled={loading}
          onChange={e => {
            updateFilters(filter, e.value)
          }}
          onHide={() => {
            applyBarFilters(filters)
          }}
        />
      </FilterItemContainer>
    )
  }

  function CalendarFilter(filter) {
    // The date value is automatically being converted to an ISO string, so I need to manually parse it back ot JS Date object(s)
    if (filter.currentValue) {
      if (Array.isArray(filter.currentValue) && filter.currentValue.length > 0) {
        filter.currentValue = filter.currentValue.map((value) => {
          return value ? new Date(value) : value
        })
      }
      else {
        filter.currentValue = new Date(filter.currentValue)
      }
    }
    if (filter.tempValue) {
      if (Array.isArray(filter.tempValue) && filter.tempValue.length > 0) {
        filter.tempValue = filter.tempValue.map((value) => {
          return value ? new Date(value) : value
        })
      }
      else {
        filter.tempValue = new Date(filter.tempValue)
      }
    }

    return (
      <FilterItemContainer key={filter.key} filter={filter}>
        <Calendar
          className={classNames(filterStyles.calendarFilter, filterStyles.filterItemBorder)}
          value={isDefined(filter.tempValue) ? filter.tempValue : filter.currentValue}
          placeholder={filter.placeholder || "mm/dd/yyyy"}
          dateFormat="mm/dd/yy"
          hideOnRangeSelection
          showTime={filter.showTime || false}
          timeOnly={filter.timeOnly || false}
          hourFormat={filter.hourFormat || "12"}
          showButtonBar
          selectionMode={filter.rangeSelect ? "range" : "single"}
          showIcon={filter.showIcon || false}
          icon={() => (
            !filter.timeOnly ?
              <i className="p-input-icon pi pi-calendar" /> :
              <i className="p-input-icon pi pi-clock" />
          )}
          onChange={e => {
            updateFilters(filter, e.value)
          }}
          onHide={() => {
            applyBarFilters(filters)
          }}
        />
      </FilterItemContainer>
    )
  }

  function CheckboxFilter(filter) {
    return (
      <div
        key={filter.key}
        className={classNames(filterStyles.checkFilter, filterStyles.filterItemBorder)}
      >
        <Checkbox
          key={filter.key}
          inputId={filter.key}
          name={filter.key}
          checked={isDefined(filter.tempValue) ? filter.tempValue : filter.currentValue}
          onChange={e => {
            const updatedFilters = updateFilters(filter, e.checked)
            applyBarFilters(updatedFilters)
          }}
        />
        <div className={filterStyles.checkFilterLabel}>
          <label
            htmlFor={filter.key}
          >
            {filter.label}
          </label>
        </div>
      </div>
    )
  }

  function getFilterElement(filter) {
    switch (filter.type) {
      case "text":
        return TextFilter(filter);

      case "multiselect":
        return MultiselectFilter(filter);

      case "dropdown":
        return DropDownFilter(filter);

      case "checkbox":
        return CheckboxFilter(filter);

      case "calendar":
        return CalendarFilter(filter);

      default:
        return null;
    }
  }

  function updateFilters(filter, value, ignoreApplied) {

    if (!isDefined(value)) value = null // Set undefined to null to handle value clear

    const updatedFilters = _.cloneDeep(filters);
    updatedFilters.forEach(item => {
      if (item.key === filter.key) {
        if ((isDefined(item.tempValue) ? item.tempValue : item.currentValue) !== value) {
          if (item.location === FilterLocation.bar) {
            item.currentValue = value
            if (!ignoreApplied) item.applied = false // Set this to track un-applied bar filters
          }
          else if (item.location === FilterLocation.panel) {
            item.tempValue = value
          }
        }
      }
    })

    setFilters(updatedFilters)

    // Handle bar dropdown clear
    if (filter.location === FilterLocation.bar && filter.type === FilterTypes.dropdown && value === null) {
      applyBarFilters(updatedFilters, true)
    }

    return updatedFilters
  }

  function resetFilters() {
    const updatedFilters = _.cloneDeep(filters)
    updatedFilters.forEach(item => {
      if (item.location === FilterLocation.panel) {
        item.tempValue = item.defaultValue
        item.currentValue = item.defaultValue
      }
    })
    setFilters(updatedFilters)
  }

  function applyBarFilters(latestFilters, barChipRemoved) {
    const hasUnappliedFilters = latestFilters.filter(item => item.location === FilterLocation.bar && item.applied === false).length > 0

    if (hasUnappliedFilters || barChipRemoved) {

      // Reset "applied" flags
      const updatedFilters = _.cloneDeep(latestFilters)
      updatedFilters.forEach(item => {
        item.applied !== undefined && delete item.applied
      })
      setFilters(updatedFilters)

      // Apply filters
      setPendingApply(true)
    }
  }

  function clearTempValues() {
    const updatedFilters = _.cloneDeep(filters)
    updatedFilters.forEach(item => {
      if (item.location === FilterLocation.panel && isDefined(item.tempValue)) {
        delete item.tempValue
      }
    })
    setFilters(updatedFilters)
  }

  function applyPanelFilters() {
    // Apply tempValues to currentValues
    const updatedFilters = _.cloneDeep(filters)
    updatedFilters.forEach(item => {
      if (item.location === FilterLocation.panel && isDefined(item.tempValue)) {
        item.currentValue = item.tempValue
        delete item.tempValue
      }
    })
    setFilters(updatedFilters)

    // Apply filters
    setPendingApply(true)
  }

  function isDefaultFilters() {
    let isDefault = true

    // Filter to "panel" filters and check each one
    filters.filter(filter => filter.location === FilterLocation.panel).forEach(filter => {
      const defaultValue = filter.defaultValue
      const currentValue = isDefined(filter.tempValue) ? filter.tempValue : filter.currentValue

      // Stop checking once isDefault === false
      if (isDefault) {
        // Check array values first - this needs special care due to how [] is evaluated
        if (Array.isArray(defaultValue) && Array.isArray(currentValue)) {
          if ((defaultValue.length > 0 || currentValue.length > 0) && !_.isEqual(defaultValue, currentValue)) isDefault = false
          else if (defaultValue.length > 0 && currentValue.length === 0) isDefault = false
          else if (defaultValue.length === 0 && currentValue.length > 0) isDefault = false
        }
        else if (defaultValue !== currentValue) { // Check other values
          isDefault = false
        }
      }
    })
    return isDefault
  }

  function allFiltersApplied() {
    return filters.filter(x => isDefined(x.tempValue)).length === 0
  }

  return (
    <>
      <div className={filterStyles.filterBar}>
        <div className={filterStyles.barFilters} >
          {/* Render FilterLocation.bar (location) items */}
          {filters?.filter(filterItem => filterItem.location === FilterLocation.bar).map(barFilterItem => getFilterElement(barFilterItem))}

          {/* Render filters button */}
          {filters?.filter(filterItem => filterItem.location === FilterLocation.panel).length > 0 &&
            <Button
              label="Filters"
              icon="pi pi-sliders-h"
              className={filterStyles.filters_button}
              onClick={() => setShowFilterPanel(true)}
              disabled={loading}
            >
              {
                ((!isDefaultFilters() && allFiltersApplied()) || (isDefaultFilters() && !allFiltersApplied())) &&
                <div style={{
                  width: "7px",
                  height: "7px",
                  backgroundColor: "purple",
                  borderRadius: "100%",
                  marginTop: "-15px",
                  marginRight: "-5px"
                }} />
              }
            </Button>
          }

          {/* Allow rendering custom components inside the filters bar */}
          {children}
        </div>

        {/* Render sorting component */}
        {sorting && (
          <CustomSort
            sorting={sorting}
            setSorting={setSorting}
            singleSortOnly={singleSortOnly}
            applySorting={applyFilters}
          />
        )}
      </div>

      <Dialog
        visible={showFilterPanel || false}
        onHide={() => {
          if (showFilterPanel) {
            setShowFilterPanel(false)
          }
        }}
        header="Filters"
        className={"custom_dialog " + filterStyles.filterDialog}
      >
        <div className={filterStyles.panelFilterContainer}>
          {filters?.filter(filterItem => filterItem.location === FilterLocation.panel).map(barFilterItem => getFilterElement(barFilterItem))}
        </div>

        <div className={filterStyles.filterDialogFooter}>
          <Button
            label={"Reset filters"}
            className="p-button-text p-button-secondary p-0 h-42px"
            type="button"
            icon="pi pi-times"
            disabled={loading || isDefaultFilters()}
            onClick={() => {
              resetFilters()
            }}
          />
          <div className={filterStyles.filterDialogButtons}>
            <Button
              className="p-button-rounded p-button-secondary w-7rem"
              type="button"
              label="Cancel"
              disabled={loading}
              onClick={() => {
                clearTempValues()
                setShowFilterPanel(false) // Hide filter panel
              }}
            />
            <Button
              className="p-button-rounded w-7rem"
              type="button"
              label="Apply"
              disabled={loading || allFiltersApplied()}
              onClick={() => {
                setShowFilterPanel(false) // Hide filter panel
                applyPanelFilters()
              }}
            />
          </div>
        </div>
      </Dialog>
    </>
  )
}

export default CustomFilter